<script setup lang="ts">
import { watch, ref } from "vue";
import useDialog from "@/composables/useDialog";
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/vue/24/outline";

const { dialogState, closeDialog } = useDialog();

const show = ref(false);
const isConfirm = ref(false);

watch(
  () => dialogState.value.visible,
  (visible) => {
    if (visible) {
      isConfirm.value = dialogState.value.type === "confirm";
      show.value = true;
    } else {
      show.value = false;
    }
  },
);

function handleConfirm() {
  show.value = false;
  setTimeout(() => closeDialog(true), 200);
}

function handleCancel() {
  show.value = false;
  setTimeout(() => closeDialog(false), 200);
}

function handleClose() {
  show.value = false;
  setTimeout(() => closeDialog(!isConfirm.value), 200);
}

function handleBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) {
    if (isConfirm.value) {
      handleCancel();
    } else {
      handleClose();
    }
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div
        v-if="show"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        @click="handleBackdropClick"
      >
        <div class="absolute inset-0 bg-brown/40 backdrop-blur-sm" />
        <div class="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 flex flex-col items-center gap-y-4 animate-dialog-in">
          <div
            class="w-14 h-14 rounded-full flex items-center justify-center"
            :class="{
              'bg-mint/30': dialogState.type === 'success',
              'bg-pink': dialogState.type === 'error',
              'bg-amber-100': dialogState.type === 'confirm',
              'bg-shell': dialogState.type === 'info',
            }"
          >
            <CheckCircleIcon
              v-if="dialogState.type === 'success'"
              class="w-8 h-8 text-mint-dark"
            />
            <ExclamationCircleIcon
              v-else-if="dialogState.type === 'error'"
              class="w-8 h-8 text-pink-dark"
            />
            <ExclamationTriangleIcon
              v-else-if="dialogState.type === 'confirm'"
              class="w-8 h-8 text-amber-600"
            />
            <InformationCircleIcon v-else class="w-8 h-8 text-brown" />
          </div>

          <h3 class="text-xl font-semibold font-odyssey italic text-brown">
            {{ dialogState.title }}
          </h3>

          <p class="text-sm text-brown/70 text-center leading-relaxed">
            {{ dialogState.message }}
          </p>

          <div v-if="isConfirm" class="mt-2 flex gap-3">
            <button
              @click="handleCancel"
              class="px-6 py-2 rounded-lg border border-brown/20 text-brown hover:bg-shell transition-colors"
            >
              Cancel
            </button>
            <button
              @click="handleConfirm"
              class="px-6 py-2 rounded-lg bg-pink-dark text-white hover:bg-pink-dark/80 transition-colors"
            >
              Delete
            </button>
          </div>
          <button
            v-else
            @click="handleClose"
            class="mt-2 button-primary !px-10"
          >
            OK
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.dialog-enter-active {
  transition: opacity 0.2s ease-out;
}
.dialog-enter-active .animate-dialog-in {
  animation: dialog-scale-in 0.25s ease-out;
}
.dialog-leave-active {
  transition: opacity 0.15s ease-in;
}
.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

@keyframes dialog-scale-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
